/**
 * MarketChart (PULSE-211, визуал 1-в-1 с CoinGlass — PULSE-381) — stacked
 * panes on one shared time axis, built on lightweight-charts v5.
 *
 * Panes (top → bottom), порядок и стиль как layout CoinGlass-TV Артёма:
 *   price    Candles + volume (legend: OHLC, Volume SMA 9)
 *   oi       Aggregated Open Interest (Candles), coins
 *   cvd      Net Delta (candles)
 *   lsr      Long/Short Ratio (Accounts), Binance
 *   liq      Aggregated Liquidations (long = red down, short = green up)
 *   funding  Funding Rates (OI Weighted), histogram
 *   taker    Futures Taker Buy/Sell Volume (Binance) + taker delta line
 *   spot     Aggregated Spot Taker Buy/Sell Volume (Binance) + delta line (PULSE-384)
 *
 * Легенда каждой панели — HTML-оверлей сверху слева (как в TradingView):
 * значения под курсором, без курсора — последний бар. Время — локальное
 * время браузера (CoinGlass показывает так же).
 */
import { useEffect, useRef, useState } from 'react'
import {
  createChart, CandlestickSeries, HistogramSeries, LineSeries,
  createSeriesMarkers, TickMarkType,
  ColorType, CrosshairMode, LineStyle, LineType,
  type IChartApi, type ISeriesApi, type UTCTimestamp, type SeriesType, type IPriceLine,
  type MouseEventParams, type Time, type ISeriesMarkersPluginApi, type SeriesMarker, type Logical,
} from 'lightweight-charts'
import type { MarketSeries } from '@/hooks/useMarketApi'

/** PULSE-405: вид «CoinGlass» — набор панелей как у Ильи в TradingView (без панелей тейкеров). */
export const CG_VIEW_OMIT = ['taker', 'spot']
export type ChartView = 'cg' | 'full'
export function useChartView(): [ChartView, (v: ChartView) => void] {
  const [v, setV] = useState<ChartView>(() => {
    try { return (localStorage.getItem('pulsar.chartView') as ChartView) || 'cg' } catch { return 'cg' }
  })
  const set = (x: ChartView) => { setV(x); try { localStorage.setItem('pulsar.chartView', x) } catch { /* private mode */ } }
  return [v, set]
}
export function ChartViewToggle({ view, onChange }: { view: ChartView; onChange: (v: ChartView) => void }) {
  return (
    <div className="flex border border-line rounded overflow-hidden" title="Набор панелей графика">
      {([['cg', 'CoinGlass'], ['full', '+ тейкеры']] as const).map(([k, label]) => (
        <button key={k} onClick={() => onChange(k)}
          className={`px-3 py-1.5 text-xs transition-colors ${view === k ? 'bg-fire-faint text-fire' : 'text-textDim hover:text-text hover:bg-elevated'}`}>{label}</button>
      ))}
    </div>
  )
}

export interface TradeMarkers {
  direction: string
  entry?: { t: number; price: number } | null
  exit?: { t: number; price: number } | null
  tp?: number | null
  sl?: number | null
  /** PULSE-231: момент сигнала — маркер на цене и точка на каждом пейне. */
  signal?: { t: number; price: number; text?: string } | null
  /** PULSE-404: дополнительные метки (доборы, частичные закрытия) — стрелка на цене + точка на пейнах. */
  extra?: { t: number; price: number; text: string; kind: 'add' | 'partial' }[] | null
}

function cssVar(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return v || fallback
}

// TradingView / CoinGlass dark palette
const CG = {
  up: '#089981', down: '#f23645',
  upVol: 'rgba(8,153,129,0.5)', downVol: 'rgba(242,54,69,0.5)',
  grid: 'rgba(42,46,57,0.6)', border: '#2a2e39', text: '#b2b5be', dim: '#787b86',
  ls: '#22ab94', delta: '#d1d4dc',
}

const compact = (p: number): string => {
  const a = Math.abs(p)
  if (a >= 1e9) return (p / 1e9).toFixed(3) + 'B'
  if (a >= 1e6) return (p / 1e6).toFixed(3) + 'M'
  if (a >= 1e3) return (p / 1e3).toFixed(3) + 'K'
  return p.toFixed(2)
}
const pxFmt = (p: number): string => {
  const a = Math.abs(p)
  if (a >= 1000) return p.toFixed(2)
  if (a >= 1) return p.toFixed(4)
  return p.toPrecision(4)
}
const pad = (n: number) => String(n).padStart(2, '0')
const TF_SEC: Record<string, number> = { '5s': 5, '15s': 15, '30s': 30, '1m': 60, '3m': 180, '5m': 300, '15m': 900, '30m': 1800, '1h': 3600, '4h': 14400 }

const PANE_ORDER = ['price', 'oi', 'cvd', 'lsr', 'liq', 'funding', 'taker', 'spot'] as const
const PANE_FACTOR: Record<string, number> = { price: 2.4, oi: 1, cvd: 1, lsr: 1, liq: 1, funding: 1, taker: 1, spot: 1 }

type Ohlc = { o: number; h: number; l: number; c: number }

/** PULSE-478: замер «линейкой» (Price Range как в TradingView/CoinGlass). */
export interface RulerMeasure {
  pane: string; from_t: number; to_t: number; from_v: number; to_v: number
  pct: number | null; delta: number; bars: number; secs: number
}
type RulerPt = { pane: string; l: number; t: number; v: number }
const PANE_RU: Record<string, string> = {
  price: 'цена', oi: 'OI', cvd: 'Net Delta', lsr: 'L/S', liq: 'ликвидации', funding: 'фандинг', taker: 'тейкеры', spot: 'спот',
}
const durRu = (s: number) => {
  const m = Math.round(Math.abs(s) / 60)
  if (m < 60) return `${m} мин`
  const h = Math.floor(m / 60)
  return h < 48 ? `${h} ч${m % 60 ? ` ${m % 60} мин` : ''}` : `${Math.floor(h / 24)} д ${h % 24} ч`
}
type Row = Record<string, number | undefined>

export function MarketChart({ data, markers, omit, onMeasure }: {
  data: MarketSeries; markers?: TradeMarkers; omit?: string[]
  /** вызывается, когда замер линейкой зафиксирован (тренажёр пишет его в журнал) */
  onMeasure?: (m: RulerMeasure) => void
}) {
  const elRef = useRef<HTMLDivElement>(null)
  const legRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const plRef = useRef<IPriceLine[]>([])
  const sRef = useRef<Record<string, ISeriesApi<SeriesType>>>({})
  const lastKeyRef = useRef<string>('')
  const shownRef = useRef<string[]>([])
  const rowsRef = useRef<Map<number, Row>>(new Map())
  const lastTRef = useRef<number | null>(null)
  const dataRef = useRef<MarketSeries | null>(null)
  // плагины меток создаются один раз и дальше только обновляются — иначе каждое обновление данных
  // (тренажёр PULSE-476: шаг воспроизведения) добавляло новый слой поверх старых
  const mkRef = useRef<Map<ISeriesApi<SeriesType>, ISeriesMarkersPluginApi<Time>>>(new Map())
  // ── Линейка (PULSE-478): точки — логические индексы баров + значение панели, рисуется SVG поверх графика
  const svgRef = useRef<SVGSVGElement>(null)
  const [rulerOn, setRulerOn] = useState(false)
  const rulerOnRef = useRef(false)
  rulerOnRef.current = rulerOn
  const measuresRef = useRef<{ a: RulerPt; b: RulerPt }[]>([])
  const pendingRef = useRef<RulerPt | null>(null)
  const cursorRef = useRef<RulerPt | null>(null)
  const onMeasureRef = useRef(onMeasure)
  onMeasureRef.current = onMeasure
  const [nMeasures, setNMeasures] = useState(0)
  const gridRef = useRef<number[]>([])                 // отсортированные времена всех серий графика = логические индексы оси

  const paneSeries = (pane: string): ISeriesApi<SeriesType> | undefined => {
    const s = sRef.current
    const pick = (...c: (ISeriesApi<SeriesType> | undefined)[]) => c.find((x) => x && x.data().length > 0) ?? c.find(Boolean)
    switch (pane) {
      case 'price': return s.candle
      case 'oi': return pick(s.oiC, s.oi)
      case 'cvd': return pick(s.cvdC, s.cvd)
      case 'lsr': return s.lsr
      case 'liq': return pick(s.liqLong, s.liqShort)
      case 'funding': return s.funding
      case 'taker': return pick(s.takerBuy, s.netd)
      case 'spot': return pick(s.spotBuy, s.spotD)
      default: return undefined
    }
  }
  const paneTop = (idx: number) => {
    const panes = chartRef.current?.panes() ?? []
    let top = 0
    for (let i = 0; i < idx; i++) top += (panes[i]?.getHeight() ?? 0) + 1
    return top
  }
  /** время бара с логическим индексом l. Ось времени библиотеки — объединение времён всех серий на графике,
   *  поэтому индекс сопоставляем со своим таким же объединением (gridRef), а не со свечами: в свечах бывают дыры,
   *  и шаг по последним свечам (180–240 с на 1m) уносил точку в будущее. За краями — шаг таймфрейма. */
  const timeAt = (l: number): number => {
    const g = gridRef.current
    if (!g.length) return 0
    const step = TF_SEC[dataRef.current?.tf ?? ''] ?? (g.length > 1 ? g[g.length - 1] - g[g.length - 2] : 60)
    if (l < 0) return g[0] + l * step
    if (l >= g.length) return g[g.length - 1] + (l - g.length + 1) * step
    return g[l]
  }
  /** точка линейки по координатам курсора внутри графика: панель — по высотам панелей, бар — логический индекс */
  const ptXY = (x: number, y: number): RulerPt | null => {
    const chart = chartRef.current
    if (!chart) return null
    const ts = chart.timeScale()
    if (x < 0 || x > ts.width()) return null                       // правая шкала цен — не график
    const panes = chart.panes()
    let top = 0, idx = -1
    for (let i = 0; i < panes.length; i++) {
      const h = panes[i].getHeight()
      if (y >= top && y < top + h) { idx = i; break }
      top += h + 1
    }
    const pane = shownRef.current[idx]
    const ser = pane ? paneSeries(pane) : undefined
    if (!ser) return null
    const v = ser.coordinateToPrice(y - top)
    const lg = ts.coordinateToLogical(x)
    if (v == null || lg == null) return null
    const l = Math.round(lg)
    return { pane, l, t: timeAt(l), v }
  }
  const measureOf = (a: RulerPt, b: RulerPt): RulerMeasure => {
    const delta = b.v - a.v
    const pct = (a.pane === 'price' || a.pane === 'oi' || (a.v > 0 && b.v > 0)) && a.v !== 0 ? (b.v / a.v - 1) * 100 : null
    return { pane: a.pane, from_t: a.t, to_t: b.t, from_v: a.v, to_v: b.v, pct, delta, bars: Math.abs(b.l - a.l), secs: b.t - a.t }
  }
  const fmtV = (pane: string, v: number) => (pane === 'price' ? pxFmt(v) : pane === 'lsr' || pane === 'funding' ? v.toFixed(4) : compact(v))
  const drawRuler = () => {
    const svg = svgRef.current
    const chart = chartRef.current
    if (!svg || !chart) return
    const items = [...measuresRef.current]
    if (pendingRef.current && cursorRef.current && cursorRef.current.pane === pendingRef.current.pane)
      items.push({ a: pendingRef.current, b: cursorRef.current })
    const ts = chart.timeScale()
    const parts: string[] = []
    for (const { a, b } of items) {
      const ser = paneSeries(a.pane)
      const idx = shownRef.current.indexOf(a.pane)
      if (!ser || idx < 0) continue
      const x1 = ts.logicalToCoordinate(a.l as Logical), x2 = ts.logicalToCoordinate(b.l as Logical)
      const y1 = ser.priceToCoordinate(a.v), y2 = ser.priceToCoordinate(b.v)
      if (x1 == null || x2 == null || y1 == null || y2 == null) continue
      const top = paneTop(idx)
      const m = measureOf(a, b)
      const up = m.delta >= 0
      const col = up ? CG.up : CG.down
      const sg = (x: number) => (x >= 0 ? '+' : '')
      const l1 = m.pct != null ? `${sg(m.pct)}${m.pct.toFixed(2)}%  (${sg(m.delta)}${fmtV(a.pane, m.delta)})` : `Δ ${sg(m.delta)}${fmtV(a.pane, m.delta)}`
      const l2 = `${m.bars} свеч. · ${durRu(m.secs)}${a.pane !== 'price' ? ` · ${PANE_RU[a.pane] ?? a.pane}` : ''}`
      const bw = Math.max(l1.length, l2.length) * 6.6 + 14
      const lx = (x1 + x2) / 2
      const by = up ? top + Math.min(y1, y2) - 40 : top + Math.max(y1, y2) + 6
      parts.push(
        `<rect x="${Math.min(x1, x2)}" y="${top + Math.min(y1, y2)}" width="${Math.max(Math.abs(x2 - x1), 1)}" height="${Math.max(Math.abs(y2 - y1), 1)}" fill="${col}" fill-opacity="0.15" stroke="${col}" stroke-opacity="0.7"/>`,
        `<line x1="${x1}" y1="${top + y1}" x2="${x2}" y2="${top + y2}" stroke="${col}" stroke-width="1.2" stroke-dasharray="4 3"/>`,
        `<rect x="${lx - bw / 2}" y="${by}" width="${bw}" height="34" rx="4" fill="${col}" fill-opacity="0.92"/>`,
        `<text x="${lx}" y="${by + 14}" fill="#fff" font-size="12" text-anchor="middle" font-family="JetBrains Mono, monospace">${l1}</text>`,
        `<text x="${lx}" y="${by + 28}" fill="#fff" fill-opacity="0.9" font-size="11" text-anchor="middle" font-family="JetBrains Mono, monospace">${l2}</text>`,
      )
    }
    svg.innerHTML = parts.join('')
  }
  const clearRuler = () => {
    measuresRef.current = []; pendingRef.current = null; cursorRef.current = null; setNMeasures(0); drawRuler()
  }

  // ── Legend: position one block per pane + fill values for time t ──
  const renderLegend = (t: number | null) => {
    const box = legRef.current
    const chart = chartRef.current
    const d = dataRef.current
    if (!box || !chart || !d) return
    const shown = shownRef.current
    const r = (t != null ? rowsRef.current.get(t) : undefined) ?? (lastTRef.current != null ? rowsRef.current.get(lastTRef.current) : undefined) ?? {}
    const v = (k: string) => r[k]
    const ohlc = (pre: string, f: (x: number) => string): string => {
      if (v(pre + 'c') == null) return ''
      const col = (v(pre + 'c') as number) >= (v(pre + 'o') as number) ? CG.up : CG.down
      return ['o', 'h', 'l', 'c'].map((x) => `<span style="color:${CG.dim}">${x.toUpperCase()}</span><span style="color:${col}">${f(v(pre + x) as number)}</span>`).join(' ')
    }
    const sign = (x: number | undefined, f: (n: number) => string) =>
      x == null ? '' : `<span style="color:${x >= 0 ? CG.up : CG.down}">${f(x)}</span>`
    const name = (s: string, sub = '') => `<span style="color:${CG.text}">${s}</span>${sub ? ` <span style="color:${CG.dim}">${sub}</span>` : ''}`
    const venue = d.venue ? d.venue[0].toUpperCase() + d.venue.slice(1) : ''
    const html: Record<string, string> = {
      price: `${name(`${d.symbol} · ${d.tf}`, venue)} &nbsp;${ohlc('p', pxFmt)}${v('pc') != null && v('po') ? ' ' + sign((v('pc') as number) - (v('po') as number), (x) => `${x >= 0 ? '+' : ''}${pxFmt(x)} (${(x / (v('po') as number) * 100).toFixed(2)}%)`) : ''}`
        + `<br/>${name('Volume', 'SMA 9')} <span style="color:${CG.text}">${v('vsma') != null ? compact(v('vsma') as number) : ''}</span>`,
      oi: `${name('Aggregated Open Interest (Candles)', 'Coins')} &nbsp;${v('oc') != null ? ohlc('o', compact) : `<span style="color:${CG.text}">${v('ov') != null ? compact(v('ov') as number) : ''}</span>`}`,
      cvd: `${name('Net Delta', venue)} &nbsp;${v('nc') != null ? ohlc('n', compact) : sign(v('nv'), compact)}`,
      lsr: `${name('Long/Short Ratio (Accounts)', 'Binance')} &nbsp;<span style="color:${(v('ls') ?? 1) >= 1 ? CG.up : CG.down}">${v('ls') != null ? (v('ls') as number).toFixed(4) : ''}</span>`,
      liq: `${name('Aggregated Liquidations')} &nbsp;<span style="color:${CG.dim}">Long</span> ${sign(v('ll'), compact)} <span style="color:${CG.dim}">Short</span> ${sign(v('ls_') != null ? -(v('ls_') as number) : undefined, compact)}`,
      funding: `${name('Funding Rates', '(Open Interest Weighted)')} &nbsp;${sign(v('fu'), (x) => x.toFixed(4))}`,
      taker: `${name('Futures Taker Buy/Sell Volume', 'Binance')} &nbsp;${sign(v('tb'), compact)} ${sign(v('td'), compact)} ${sign(v('ts') != null ? -(v('ts') as number) : undefined, compact)}`,
      spot: `${name('Aggregated Spot Taker Buy/Sell Volume', 'Binance')} &nbsp;${sign(v('sb'), compact)} ${sign(v('sb') != null && v('ss') != null ? (v('sb') as number) - (v('ss') as number) : undefined, compact)} ${sign(v('ss') != null ? -(v('ss') as number) : undefined, compact)}`,
    }
    const panes = chart.panes()
    let top = 0
    const parts: string[] = []
    shown.forEach((p, i) => {
      const h = panes[i]?.getHeight() ?? 0
      parts.push(`<div style="position:absolute;left:6px;top:${top + 4}px;right:70px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:15px">${html[p] ?? ''}</div>`)
      top += h + 1
    })
    box.innerHTML = parts.join('')
  }

  // ── Create the chart + series exactly once ───────────────────────
  useEffect(() => {
    if (!elRef.current) return
    const localTime = (t: number) => new Date(t * 1000)
    const chart = createChart(elRef.current, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: CG.dim,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, Ubuntu, sans-serif',
        fontSize: 11,
        attributionLogo: false,
        panes: { separatorColor: CG.border, separatorHoverColor: CG.border, enableResize: true },
      },
      grid: {
        vertLines: { color: CG.grid, style: LineStyle.Solid },
        horzLines: { color: CG.grid, style: LineStyle.Solid },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: CG.border, scaleMargins: { top: 0.12, bottom: 0.08 } },
      localization: {
        timeFormatter: (t: Time) => {
          const d = localTime(t as number)
          return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
        },
      },
      timeScale: {
        borderColor: CG.border, timeVisible: true, secondsVisible: false, rightOffset: 4, barSpacing: 6, minBarSpacing: 1,
        tickMarkFormatter: (t: Time, type: TickMarkType) => {
          const d = localTime(t as number)
          if (type === TickMarkType.Year) return String(d.getFullYear())
          if (type === TickMarkType.Month) return d.toLocaleString('ru-RU', { month: 'short' })
          if (type === TickMarkType.DayOfMonth) return String(d.getDate())
          return `${pad(d.getHours())}:${pad(d.getMinutes())}`
        },
      },
    })
    chartRef.current = chart

    const big = { type: 'custom' as const, formatter: compact, minMove: 0.000001 }
    const shown: string[] = PANE_ORDER.filter((p) => !(omit ?? []).includes(p))
    shownRef.current = shown
    const pidx = (name: string) => shown.indexOf(name)
    const candleOpts = {
      upColor: CG.up, downColor: CG.down, borderVisible: false,
      wickUpColor: CG.up, wickDownColor: CG.down, priceLineVisible: false, lastValueVisible: true,
    }

    sRef.current.candle = chart.addSeries(CandlestickSeries, {
      ...candleOpts, priceLineVisible: true, priceFormat: { type: 'custom', formatter: pxFmt, minMove: 0.0000001 },
    }, 0)
    sRef.current.vol = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' }, priceScaleId: 'vol', lastValueVisible: false, priceLineVisible: false,
    }, 0)
    chart.priceScale('vol').applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } })

    if (shown.includes('oi')) {
      sRef.current.oi = chart.addSeries(LineSeries, { color: CG.up, lineWidth: 1, priceLineVisible: false, priceFormat: big }, pidx('oi'))
      sRef.current.oiC = chart.addSeries(CandlestickSeries, { ...candleOpts, priceFormat: big }, pidx('oi'))
    }
    if (shown.includes('cvd')) {
      sRef.current.cvd = chart.addSeries(LineSeries, { color: CG.up, lineWidth: 1, priceLineVisible: false, priceFormat: big }, pidx('cvd'))
      sRef.current.cvdC = chart.addSeries(CandlestickSeries, { ...candleOpts, priceFormat: big }, pidx('cvd'))
    }
    if (shown.includes('lsr')) sRef.current.lsr = chart.addSeries(LineSeries, {
      color: CG.ls, lineWidth: 1, priceLineVisible: false, lineType: LineType.WithSteps,   // ступеньки, как в CoinGlass
      priceFormat: { type: 'custom', formatter: (p: number) => p.toFixed(4), minMove: 0.0001 },
    }, pidx('lsr'))
    if (shown.includes('liq')) {
      // PULSE-405: как CoinGlass Aggregated Liquidations — ликвидации ЛОНГОВ вверх зелёным, ШОРТОВ вниз красным
      sRef.current.liqLong = chart.addSeries(HistogramSeries, { color: CG.up, priceFormat: big, priceLineVisible: false }, pidx('liq'))
      sRef.current.liqShort = chart.addSeries(HistogramSeries, { color: CG.down, priceFormat: big, priceLineVisible: false, lastValueVisible: false }, pidx('liq'))
    }
    if (shown.includes('funding')) sRef.current.funding = chart.addSeries(HistogramSeries, {
      priceLineVisible: false,
      priceFormat: { type: 'custom', formatter: (p: number) => p.toFixed(4), minMove: 0.0001 },
    }, pidx('funding'))
    if (shown.includes('taker')) {
      sRef.current.takerBuy = chart.addSeries(HistogramSeries, { color: CG.up, priceFormat: big, priceLineVisible: false }, pidx('taker'))
      sRef.current.takerSell = chart.addSeries(HistogramSeries, { color: CG.down, priceFormat: big, priceLineVisible: false }, pidx('taker'))
      sRef.current.netd = chart.addSeries(LineSeries, { color: CG.delta, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, priceFormat: big }, pidx('taker'))
    }
    if (shown.includes('spot')) {
      sRef.current.spotBuy = chart.addSeries(HistogramSeries, { color: CG.up, priceFormat: big, priceLineVisible: false }, pidx('spot'))
      sRef.current.spotSell = chart.addSeries(HistogramSeries, { color: CG.down, priceFormat: big, priceLineVisible: false }, pidx('spot'))
      sRef.current.spotD = chart.addSeries(LineSeries, { color: CG.delta, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, priceFormat: big }, pidx('spot'))
    }

    try {
      chart.panes().forEach((pn, i) => pn.setStretchFactor(PANE_FACTOR[shown[i]] ?? 1))
    } catch { /* older API — ignore */ }

    const onMove = (p: MouseEventParams) => renderLegend(p.time != null ? (p.time as number) : null)
    chart.subscribeCrosshairMove(onMove)
    // линейка: клик в режиме линейки или Shift+клик — начало, второй клик на той же панели — конец.
    // Нативные pointer-события: subscribeClick теряет быстрый второй клик (уходит в dblclick с точкой первого),
    // перетаскивание графика (сдвиг > 5px) точкой не считается.
    const el = elRef.current
    const local = (e: PointerEvent) => { const r = el.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top } }
    let down: { x: number; y: number } | null = null
    const onDown = (e: PointerEvent) => { down = local(e) }
    const onUp = (e: PointerEvent) => {
      const at = local(e)
      const moved = !down || Math.hypot(at.x - down.x, at.y - down.y) > 5
      down = null
      if (moved || !(rulerOnRef.current || e.shiftKey)) return
      const pt = ptXY(at.x, at.y)
      if (!pt) return
      const pend = pendingRef.current
      if (!pend || pend.pane !== pt.pane) {
        pendingRef.current = pt; cursorRef.current = pt
      } else {
        measuresRef.current = [...measuresRef.current, { a: pend, b: pt }]
        pendingRef.current = null
        setNMeasures(measuresRef.current.length)
        if (pt.l !== pend.l) onMeasureRef.current?.(measureOf(pend, pt))
      }
      drawRuler()
    }
    const onPMove = (e: PointerEvent) => {
      if (!pendingRef.current) return
      const at = local(e)
      const pt = ptXY(at.x, at.y)
      if (pt) cursorRef.current = pt
      drawRuler()
    }
    el.addEventListener('pointerdown', onDown, true)
    el.addEventListener('pointerup', onUp, true)
    el.addEventListener('pointermove', onPMove, true)
    const onRange = () => drawRuler()
    chart.timeScale().subscribeVisibleLogicalRangeChange(onRange)
    const kd = (e: KeyboardEvent) => { if (e.key === 'Escape' && pendingRef.current) { pendingRef.current = null; drawRuler() } }
    window.addEventListener('keydown', kd)
    const ro = new ResizeObserver(() => requestAnimationFrame(() => { renderLegend(null); drawRuler() }))
    ro.observe(elRef.current)

    return () => {
      ro.disconnect(); chart.unsubscribeCrosshairMove(onMove)
      el.removeEventListener('pointerdown', onDown, true); el.removeEventListener('pointerup', onUp, true); el.removeEventListener('pointermove', onPMove, true)
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(onRange)
      window.removeEventListener('keydown', kd)
      chart.remove(); chartRef.current = null; sRef.current = {}; lastKeyRef.current = ''; mkRef.current = new Map()
    }
  }, [])

  // ── Push data on every change (preserve zoom after first fit) ─────
  useEffect(() => {
    const s = sRef.current
    if (!chartRef.current || !s.candle || !data) return
    dataRef.current = data
    const T = (t: number) => t as UTCTimestamp
    const rows = new Map<number, Row>()
    const put = (t: number, kv: Row) => rows.set(t, { ...(rows.get(t) ?? {}), ...kv })

    s.candle.setData(data.candles.map((c) => ({ time: T(c.t), open: c.o, high: c.h, low: c.l, close: c.c })))
    s.vol.setData(data.candles.map((c) => ({ time: T(c.t), value: c.v, color: c.c >= c.o ? CG.upVol : CG.downVol })))
    data.candles.forEach((c, i) => {
      const w = data.candles.slice(Math.max(0, i - 8), i + 1)
      put(c.t, { po: c.o, ph: c.h, pl: c.l, pc: c.c, vsma: w.reduce((a, k) => a + k.v, 0) / w.length })
    })

    const setOhlcOrLine = <P extends Partial<Ohlc> & { t: number }>(line: ISeriesApi<SeriesType> | undefined,
      cand: ISeriesApi<SeriesType> | undefined, pts: P[], val: (p: P) => number) => {
      const withO = pts.filter((p) => p.o != null)
      if (cand && withO.length > 0) {
        cand.setData(withO.map((p) => ({ time: T(p.t), open: p.o as number, high: p.h as number, low: p.l as number, close: p.c as number })))
        if (line) line.setData([])
      } else {
        if (line) line.setData(pts.map((p) => ({ time: T(p.t), value: val(p) })))
        if (cand) cand.setData([])
      }
    }
    setOhlcOrLine(s.oi, s.oiC, data.oi, (p) => p.v)
    data.oi.forEach((p) => put(p.t, { ov: p.v, oo: p.o, oh: p.h, ol: p.l, oc: p.c }))
    setOhlcOrLine(s.cvd, s.cvdC, data.cvd, (p) => p.cvd)
    data.cvd.forEach((p) => put(p.t, { nv: p.cvd, no: p.o, nh: p.h, nl: p.l, nc: p.c, td: p.td }))

    // L/S как в CoinGlass: зелёная при перевесе лонгов (≥1), красная — шортов (QUSDT 0.59 у CG красная)
    if (s.lsr) s.lsr.setData(data.lsr.filter((p) => p.ratio != null).map((p) => ({ time: T(p.t), value: p.ratio as number, color: (p.ratio as number) >= 1 ? CG.up : CG.down })))
    data.lsr.forEach((p) => { if (p.ratio != null) put(p.t, { ls: p.ratio }) })
    if (s.liqLong) s.liqLong.setData(data.liq.map((p) => ({ time: T(p.t), value: p.long })))
    if (s.liqShort) s.liqShort.setData(data.liq.map((p) => ({ time: T(p.t), value: -p.short })))
    data.liq.forEach((p) => put(p.t, { ll: p.long, ls_: p.short }))
    if (s.funding) s.funding.setData(data.funding.map((p) => ({ time: T(p.t), value: p.v * 100, color: p.v >= 0 ? CG.up : CG.down })))
    data.funding.forEach((p) => put(p.t, { fu: p.v * 100 }))
    if (s.takerBuy) s.takerBuy.setData((data.taker ?? []).map((p) => ({ time: T(p.t), value: p.buy })))
    if (s.takerSell) s.takerSell.setData((data.taker ?? []).map((p) => ({ time: T(p.t), value: -p.sell })))
    ;(data.taker ?? []).forEach((p) => put(p.t, { tb: p.buy, ts: p.sell }))
    if (s.netd) s.netd.setData(data.cvd.map((p) => ({ time: T(p.t), value: p.td ?? p.nd })))
    const spot = data.spot_taker ?? []
    if (s.spotBuy) s.spotBuy.setData(spot.map((p) => ({ time: T(p.t), value: p.buy })))
    if (s.spotSell) s.spotSell.setData(spot.map((p) => ({ time: T(p.t), value: -p.sell })))
    if (s.spotD) s.spotD.setData(spot.map((p) => ({ time: T(p.t), value: p.buy - p.sell })))
    spot.forEach((p) => put(p.t, { sb: p.buy, ss: p.sell }))

    rowsRef.current = rows
    {
      const sh = shownRef.current, ts = new Set<number>()
      const add = (arr?: { t: number }[]) => (arr ?? []).forEach((p) => ts.add(p.t))
      add(data.candles)
      if (sh.includes('oi')) add(data.oi)
      if (sh.includes('cvd') || sh.includes('taker')) add(data.cvd)
      if (sh.includes('lsr')) add(data.lsr.filter((p) => p.ratio != null))
      if (sh.includes('liq')) add(data.liq)
      if (sh.includes('funding')) add(data.funding)
      if (sh.includes('taker')) add(data.taker)
      if (sh.includes('spot')) add(data.spot_taker)
      gridRef.current = [...ts].sort((a, b) => a - b)
    }
    lastTRef.current = data.candles.length ? data.candles[data.candles.length - 1].t
      : (data.oi.length ? data.oi[data.oi.length - 1].t : null)

    // Re-frame only when the symbol/tf changes — a 30s data refresh
    // keeps the user's current zoom/pan instead of snapping back.
    const key = `${data.symbol}:${data.tf}`
    if (key !== lastKeyRef.current) {
      chartRef.current.timeScale().fitContent()
      // другая монета/тф — логические индексы другие, старые замеры теряют смысл
      if (lastKeyRef.current) { measuresRef.current = []; pendingRef.current = null; setNMeasures(0) }
      lastKeyRef.current = key
    }
    requestAnimationFrame(() => { renderLegend(null); drawRuler() })
  }, [data])

  // ── Entry/exit markers + TP/SL price lines on the price pane ──────
  useEffect(() => {
    const s = sRef.current
    if (!chartRef.current || !s.candle || !markers || !data?.candles?.length) return
    const T = (t: number) => t as UTCTimestamp
    const times = data.candles.map((c) => c.t)
    const snap = (t: number) => times.reduce((a, b) => (Math.abs(b - t) < Math.abs(a - t) ? b : a), times[0])
    const C = {
      pulse: cssVar('--pulse', '#84CC16'), rose: cssVar('--rose', '#F43F5E'),
      sky: cssVar('--sky', '#38BDF8'), dim: cssVar('--text-dim', '#8893A4'),
      fire: cssVar('--fire', '#FF7A52'),
    }
    type Mk = { time: UTCTimestamp; position: 'belowBar' | 'aboveBar'; color: string; shape: 'arrowUp' | 'arrowDown'; text: string }
    const mk: Mk[] = []
    if (markers.signal) mk.push({ time: T(snap(markers.signal.t)), position: markers.direction === 'short' ? 'aboveBar' : 'belowBar', color: C.fire, shape: markers.direction === 'short' ? 'arrowDown' : 'arrowUp', text: markers.signal.text ?? 'сигнал' })
    if (markers.entry) mk.push({ time: T(snap(markers.entry.t)), position: 'belowBar', color: C.sky, shape: 'arrowUp', text: `вход ${markers.entry.price}` })
    if (markers.exit) mk.push({ time: T(snap(markers.exit.t)), position: 'aboveBar', color: markers.direction === 'short' ? C.rose : C.pulse, shape: 'arrowDown', text: `выход ${markers.exit.price}` })
    ;(markers.extra ?? []).forEach((x) => mk.push({
      time: T(snap(x.t)), position: x.kind === 'add' ? 'aboveBar' : 'belowBar',
      color: x.kind === 'add' ? C.fire : C.dim, shape: x.kind === 'add' ? 'arrowDown' : 'arrowUp', text: x.text,
    }))
    mk.sort((a, b) => (a.time as number) - (b.time as number))
    const setMk = (ser: ISeriesApi<SeriesType>, list: SeriesMarker<Time>[]) => {
      const pl = mkRef.current.get(ser)
      if (pl) pl.setMarkers(list); else mkRef.current.set(ser, createSeriesMarkers<Time>(ser, list))
    }
    setMk(s.candle, mk)

    // Same entry/exit moment marked on every context pane (compact dots),
    // so the trade is locatable across OI/CVD/L-S/liq/funding, not just price.
    const exitColor = markers.direction === 'short' ? C.rose : C.pulse
    type Dot = { time: UTCTimestamp; position: 'inBar'; color: string; shape: 'circle'; text: string }
    const dots: Dot[] = []
    if (markers.signal) dots.push({ time: T(snap(markers.signal.t)), position: 'inBar', color: C.fire, shape: 'circle', text: '' })
    if (markers.entry) dots.push({ time: T(snap(markers.entry.t)), position: 'inBar', color: C.sky, shape: 'circle', text: '' })
    if (markers.exit) dots.push({ time: T(snap(markers.exit.t)), position: 'inBar', color: exitColor, shape: 'circle', text: '' })
    ;(markers.extra ?? []).forEach((x) => dots.push({ time: T(snap(x.t)), position: 'inBar', color: x.kind === 'add' ? C.fire : C.dim, shape: 'circle', text: '' }))
    dots.sort((a, b) => (a.time as number) - (b.time as number))
    ;[s.oi, s.oiC, s.cvd, s.cvdC, s.takerBuy, s.lsr, s.liqLong, s.funding].forEach((ser) => { if (ser) setMk(ser, dots) })

    plRef.current.forEach((l) => { try { s.candle.removePriceLine(l) } catch { /* gone */ } })
    plRef.current = []
    const addLine = (price: number | null | undefined, color: string, title: string, dashed = true) => {
      if (price == null) return
      plRef.current.push(s.candle.createPriceLine({
        price, color, lineWidth: 1, lineStyle: dashed ? LineStyle.Dashed : LineStyle.Solid,
        axisLabelVisible: true, title,
      }))
    }
    addLine(markers.signal?.price, C.fire, markers.signal?.text ?? 'сигнал')
    addLine(markers.entry?.price, C.sky, 'вход', false)
    addLine(markers.exit?.price, markers.direction === 'short' ? C.rose : C.pulse, 'выход', false)
    addLine(markers.tp, C.pulse, 'TP')
    addLine(markers.sl, C.rose, 'SL')
  }, [data, markers])

  return (
    <div className="w-full h-full" style={{ position: 'relative' }}>
      <div ref={elRef} style={{ position: 'absolute', inset: 0, cursor: rulerOn ? 'crosshair' : undefined }} />
      <svg ref={svgRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 2, overflow: 'visible' }} />
      <div style={{ position: 'absolute', top: 4, right: 72, zIndex: 4, display: 'flex', gap: 4 }}>
        <button type="button" onClick={() => { setRulerOn(!rulerOn); pendingRef.current = null; drawRuler() }}
          title="Линейка: клик — начало, клик — конец (без режима — Shift+клик). Esc — отменить незаконченный замер"
          style={{ background: rulerOn ? 'rgba(41,98,255,.35)' : 'rgba(30,34,45,.85)', color: CG.text, border: `1px solid ${rulerOn ? '#2962ff' : CG.border}`,
            borderRadius: 4, padding: '2px 7px', fontSize: 12, cursor: 'pointer' }}>📏 линейка</button>
        {nMeasures > 0 && (
          <button type="button" onClick={clearRuler} title="Убрать все замеры"
            style={{ background: 'rgba(30,34,45,.85)', color: CG.text, border: `1px solid ${CG.border}`, borderRadius: 4, padding: '2px 7px', fontSize: 12, cursor: 'pointer' }}>✕ {nMeasures}</button>
        )}
      </div>
      <div ref={legRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 3, fontSize: 12,
        textShadow: '0 0 3px #131722, 0 0 3px #131722',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, Ubuntu, sans-serif' }} />
    </div>
  )
}
